// Charts: a fenced block whose info string is `chart`, in a post or in a
// chapter's commentary. The author's own numbers, drawn as bars —
//
//   ```chart
//   title: Years each king of Judah reigned
//   note: As 1–2 Kings count them
//   unit: years
//   Rehoboam: 17
//   Abijah: 3
//   ```
//
// — or spans of years on a timeline, which is what a row of dates makes it —
//
//   ```chart
//   title: The kings of Judah
//   Rehoboam: 931–913 BC
//   The fall of Jerusalem: 586 BC
//   ```
//
// — or a Hebrew or Greek word counted through the text, book by book, or
// chapter by chapter given `in:` —
//
//   ```chart
//   title: Where hesed is used
//   word: H2617
//   in: Psalms
//   ```
//
// None holds scripture. A chart is numbers, and a word chart counts the
// public-domain interlinear by Strong's number: the ESV is not ours to keep,
// so it is not ours to count either.
import { BOOK_LOOKUP } from "./books.js";
/** Past this a chart is a table; say so rather than draw a wall of bars. */
export const MAX_CHART_ROWS = 80;
/** The lines that set a chart up rather than add a row to it. */
const OPTIONS = new Set(["title", "note", "unit", "word", "in"]);
const STRONGS = /^[HG]\d{1,5}$/;
/** A row that is a date rather than a number: it names an era, or runs from one year to another. */
const ERA = /\b(?:BC|BCE|AD|CE)\b/i;
// A digit before the dash, so "-3" stays a negative number rather than half a span.
const SPAN = /\d\s*[–—-]\s*(?:AD|CE)?\s*\d/i;
const YEAR = /^(?:(?:c|ca|circa)\.?\s*)?(?:(AD|CE)\s*)?(\d{1,4})\s*(BC|BCE|AD|CE)?$/i;
/** The same folding `BOOK_LOOKUP` is keyed by. */
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
const fail = (error) => ({ ok: false, error });
function yearOf(raw) {
    const m = YEAR.exec(raw.trim());
    if (!m || (m[1] && m[3]))
        return null;
    const era = m[1] ?? m[3];
    return { n: Number(m[2]), era: era ? (/^b/i.test(era) ? "BC" : "AD") : null };
}
/**
 * "931–913 BC", "AD 30–33", "586 BC" as signed years, or what is wrong with
 * it. An era written once holds for both ends; a span with none is AD, as a
 * bare year is in English. A single year must say which era it is in, so
 * that "Asa: 41" in a timeline is caught rather than drawn in AD 41.
 */
function yearsOf(value) {
    const parts = value.split(/\s*[–—-]\s*/);
    const years = parts.length <= 2 ? parts.map(yearOf) : [null];
    if (years.some((y) => !y))
        return "is not a year or a span of years, such as “911–870 BC”";
    const [a, b = a] = years;
    if (parts.length === 1 && !a.era)
        return "needs BC or AD to sit on a timeline";
    if (a.n === 0 || b.n === 0)
        return "names a year 0, and there is none: 1 BC is followed by AD 1";
    const signed = (y, other) => ((y.era ?? other ?? "AD") === "BC" ? -y.n : y.n);
    const start = signed(a, b.era);
    const end = signed(b, a.era);
    if (end < start)
        return "ends before it starts";
    return { start, end };
}
/**
 * A chart block read into what the page draws, or why it cannot be. A row is
 * a label, a colon and a value; the label runs to the *last* colon, so
 * "Psalm 23:1: 4" is a row labelled "Psalm 23:1".
 */
export function parseChart(source) {
    const options = new Map();
    const rows = [];
    for (const raw of source.split("\n")) {
        const line = raw.trim();
        if (!line)
            continue;
        const option = /^([a-z]+)\s*:(.*)$/i.exec(line);
        if (option && OPTIONS.has(option[1].toLowerCase())) {
            options.set(option[1].toLowerCase(), option[2].trim());
            continue;
        }
        const colon = line.lastIndexOf(":");
        const label = line.slice(0, Math.max(0, colon)).trim();
        if (colon < 0 || !label) {
            return fail(`“${line}” is not a row: give a label, a colon and a value, such as “Asa: 41” or “Asa: 911–870 BC”`);
        }
        rows.push({ line, label, value: line.slice(colon + 1).trim() });
    }
    const title = options.get("title") ?? "";
    if (!title)
        return fail("A chart needs a “title:” line saying what it shows");
    const note = options.get("note") ?? "";
    const within = options.get("in");
    const asked = options.get("word");
    if (asked !== undefined) {
        const word = asked.toUpperCase();
        if (!STRONGS.test(word))
            return fail(`“${asked}” is not a Strong’s number: they look like H2617 or G26`);
        if (rows.length)
            return fail("A chart that counts a word takes no rows of its own");
        if (options.has("unit"))
            return fail("“unit:” is for a chart of your own numbers; a word chart counts occurrences");
        let book;
        if (within !== undefined) {
            book = BOOK_LOOKUP.get(norm(within));
            if (!book)
                return fail(`There is no book called “${within}”`);
            const testament = word.startsWith("H") ? "OT" : "NT";
            if (book.testament !== testament) {
                return fail(testament === "OT"
                    ? `${word} is an Old Testament word, and ${book.name} is in the New`
                    : `${word} is a New Testament word, and ${book.name} is in the Old`);
            }
        }
        return { ok: true, chart: { kind: "usage", title, note, word, book: book?.id ?? null } };
    }
    if (within !== undefined)
        return fail("“in:” narrows a word chart to one book, so it needs a “word:” line");
    if (!rows.length)
        return fail("A chart needs rows, such as “Asa: 41”, or a “word:” line to count");
    if (rows.length > MAX_CHART_ROWS) {
        return fail(`A chart can have at most ${MAX_CHART_ROWS} rows, and this one has ${rows.length}`);
    }
    if (rows.some((r) => ERA.test(r.value) || SPAN.test(r.value))) {
        if (options.has("unit"))
            return fail("“unit:” is for a chart of numbers; a timeline counts in years");
        const spans = [];
        for (const row of rows) {
            const years = yearsOf(row.value);
            if (typeof years === "string")
                return fail(`“${row.line}” ${years}`);
            spans.push({ label: row.label, ...years, when: row.value });
        }
        return { ok: true, chart: { kind: "timeline", title, note, rows: spans } };
    }
    const bars = [];
    for (const row of rows) {
        const value = row.value.replace(/,/g, "");
        if (!/^-?\d+(?:\.\d+)?$/.test(value))
            return fail(`“${row.line}” does not end in a number`);
        if (Number(value) < 0)
            return fail(`Bars count up from nothing, so “${row.label}” cannot be ${value}`);
        bars.push({ label: row.label, value: Number(value) });
    }
    return { ok: true, chart: { kind: "bars", title, note, unit: options.get("unit") ?? "", rows: bars } };
}
//# sourceMappingURL=charts.js.map